/**
 * Census reverse geography lookup — names the school district a coordinate falls
 * in. Used only on the "not in any AIU member district" path so we can tell the
 * user which (non-member) district the address actually belongs to, instead of a
 * dead-end "not found". Best-effort: returns null on any failure (including CORS).
 */

export interface CensusDistrict {
  name: string;
  type: 'Unified' | 'Elementary' | 'Secondary';
}

const LAYERS: ReadonlyArray<readonly [string, CensusDistrict['type']]> = [
  ['Unified School Districts', 'Unified'],
  ['Secondary School Districts', 'Secondary'],
  ['Elementary School Districts', 'Elementary'],
];

export async function reverseSchoolDistrict(
  lat: number,
  lng: number,
  signal?: AbortSignal
): Promise<CensusDistrict | null> {
  try {
    const params = new URLSearchParams({
      x: String(lng),
      y: String(lat),
      benchmark: 'Public_AR_Current',
      vintage: 'Current_Current',
      layers: LAYERS.map((l) => l[0]).join(','),
      format: 'json',
    });
    const res = await fetch(
      `https://geocoding.geo.census.gov/geocoder/geographies/coordinates?${params.toString()}`,
      { signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const geos = data?.result?.geographies || {};
    for (const [layer, type] of LAYERS) {
      const arr = geos[layer];
      if (Array.isArray(arr) && arr.length > 0 && arr[0].NAME) {
        return { name: arr[0].NAME, type };
      }
    }
    return null;
  } catch {
    return null;
  }
}
